import { useCallback, useEffect, useRef } from 'react';
import { useBlocker as useRouterBlocker } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useNotifilcationDialog } from '@src/app/notifications/dialog/hooks';

type UseBlockerParams = {
  when: boolean;
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onStay?: () => void;
  skipPathPrefixes?: string[];
};

const DEFAULT_SKIP_PATH_PREFIXES = ['/auth/login'];

function useBlocker({
  when,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onStay,
  skipPathPrefixes = DEFAULT_SKIP_PATH_PREFIXES,
}: UseBlockerParams) {
  const { t } = useTranslation();
  const dialog = useNotifilcationDialog();
  const resolvedTitle = title ?? t('blocker.title', 'Unsaved Changes');
  const resolvedMessage = message ?? t(
    'blocker.text',
    'You have unsaved changes. Discard them and continue?',
  );
  const resolvedConfirmLabel = confirmLabel ?? t('blocker.confirm_button', 'Keep editing');
  const resolvedCancelLabel = cancelLabel ?? t('blocker.cancel_button', 'Discard changes');
  const blocker = useRouterBlocker({
    shouldBlockFn: ({ next }) => {
      if (!when) {
        return false;
      }

      return !skipPathPrefixes.some((prefix) => next.pathname.startsWith(prefix));
    },
    withResolver: true,
    enableBeforeUnload: when,
  });
  const isConfirmOpen = useRef(false);
  const confirmPromise = useRef<Promise<boolean> | null>(null);
  const confirmLeave = useCallback(async () => {
    if (confirmPromise.current) {
      return confirmPromise.current;
    }

    confirmPromise.current = (async () => {
      const { action } = await dialog.open({
        title: resolvedTitle,
        children: resolvedMessage,
        slotProps: {
          confirmButton: {
            label: resolvedConfirmLabel,
            autoFocus: true,
          },
          cancelButton: { label: resolvedCancelLabel },
        },
      });

      if (action === dialog.NOTIFICATION_DIALOG_ACTIONS.CANCEL) {
        return true;
      }

      onStay?.();
      return false;
    })();

    try {
      return await confirmPromise.current;
    } finally {
      window.setTimeout(() => {
        confirmPromise.current = null;
      }, 0);
    }
  }, [
    dialog,
    onStay,
    resolvedCancelLabel,
    resolvedConfirmLabel,
    resolvedMessage,
    resolvedTitle,
  ]);

  useEffect(() => {
    if (blocker.status !== 'blocked' || isConfirmOpen.current) {
      return;
    }

    isConfirmOpen.current = true;
    void (async () => {
      const canLeave = await confirmLeave();

      isConfirmOpen.current = false;
      if (canLeave) {
        blocker.proceed?.();
        return;
      }
      blocker.reset?.();
    })();
  }, [blocker, confirmLeave]);

  return confirmLeave;
}

export default useBlocker;
